import { useMemo, type FC } from "react";
import { durationBetweenTimestamps } from "@/format/time";
import { type PlainMessage } from "@bufbuild/protobuf";
import { Text } from "@/components/podkit/typography/Text";
import { WorkflowExecutionActionStepStatusIcon } from "@/components/automations/AutomationExecutionActionStepStatusIcon";
import { AutomationStepKindPill } from "@/components/automations/AutomationStepKindPill";
import type { WorkflowExecutionAction_Status_StepStatus } from "gitpod-next-api/gitpod/v1/workflow_pb";
import type { PillVariant } from "@/components/podkit/status/Pill";
import IconConsoleSimple from "central-icons/IconConsoleSimple";
import IconBubbleText6 from "central-icons/IconBubbleText6";
import IconPullRequest from "central-icons/IconPullRequest";
import IconTable from "central-icons/IconTable";
import { workflowExecutionActionStepPhaseLabel } from "@/utils/automations";

type StepInfo = {
    type: string;
    icon: typeof IconConsoleSimple;
    variant: PillVariant;
    content: string;
};

function getStepInfo(step: PlainMessage<WorkflowExecutionAction_Status_StepStatus>): StepInfo | null {
    const stepCase = step.step?.step;
    if (!stepCase) return null;

    switch (stepCase.case) {
        case "task":
            return {
                type: "Shell script",
                icon: IconConsoleSimple,
                variant: "warning",
                content: stepCase.value.command || "",
            };
        case "agent":
            return {
                type: "Prompt",
                icon: IconBubbleText6,
                variant: "brand",
                content: stepCase.value.prompt || "",
            };
        case "pullRequest":
            return {
                type: "Pull request",
                icon: IconPullRequest,
                variant: "success",
                content: stepCase.value.title || "",
            };
        case "report": {
            const outputs = stepCase.value.outputs || [];
            return {
                type: "Report",
                icon: IconTable,
                variant: "neutral",
                content: outputs.length > 0 ? outputs.map((o) => o.title || o.key).join(", ") : "No outputs defined",
            };
        }
        default:
            return null;
    }
}

export const WorkflowExecutionActionStepCard: FC<{
    step: PlainMessage<WorkflowExecutionAction_Status_StepStatus>;
}> = ({ step }) => {
    const duration =
        step.startedAt && step.finishedAt ? durationBetweenTimestamps(step.startedAt, step.finishedAt) : undefined;
    const durationText = duration || workflowExecutionActionStepPhaseLabel(step.phase, step.failureMessage);

    const stepInfo = useMemo(() => getStepInfo(step), [step]);

    return (
        <div className="flex items-center gap-3 rounded-lg border border-border-base bg-surface-secondary p-3">
            {/* Left: Type pill and content stacked */}
            <div className="flex min-w-0 flex-1 flex-col gap-2">
                {stepInfo && (
                    <>
                        <AutomationStepKindPill
                            variant={stepInfo.variant}
                            LeadingIcon={stepInfo.icon}
                            className="w-fit"
                        >
                            {stepInfo.type}
                        </AutomationStepKindPill>
                        <Text className="whitespace-pre-wrap break-words font-mono text-sm text-content-secondary">
                            {stepInfo.content}
                        </Text>
                    </>
                )}
            </div>

            {/* Right: Duration/error and status icon */}
            <div className="flex shrink-0 items-center gap-3">
                {step.failureMessage ? (
                    <Text className="text-base text-content-destructive">{step.failureMessage}</Text>
                ) : (
                    <Text className="text-base text-content-muted">{durationText}</Text>
                )}
                <WorkflowExecutionActionStepStatusIcon step={step} size="base" />
            </div>
        </div>
    );
};
